import { TagStore } from './TagStore.js';
import { TagBoxModel } from './TagBoxModel.js';
import { TagEventLinker } from './controller/TagEventLinker.js';
import { TagBoxJQueryAdapter } from './adapter/TagBoxJQueryAdapter.js';
import { resolveTagInteractions } from './tagInteractions.js';
import { buildRedrawPatch, isDuplicateDrawEvent } from './tagRedrawPatch.js';
import { TagSaveQueue, createSequentialBatchSender } from './TagSaveQueue.js';
import { isSendableTag, unsendableReason } from './tagSendable.js';
import { findDomStoreMismatches, keywordKey } from './tagDomCheck.js';
import { FLASH_MS } from './view/TagBoxView.js';

// Reason for a box whose last send failed (next to the UnsendableReason values, see getUnsavedReasons).
export const SAVE_FAILED_REASON = 'save-failed';

export class TaggingCore {
    // Live cores per document. Events are scoped by docId only, so a second core for the same document hears every
    // event of the first and boxes get saved twice (bug:tagging-duplicate-core-boxes-without-meta). Diagnostic only:
    // warn with a stack trace, never block — the trace shows where the second core came from.
    static #liveCoresByDoc = new Map();
    #destroyed = false;
    #tagStore;
    #mediator;
    #docId;
    #persistence;
    #services = {};
    #captureMetadata = null;
    #tagFilter = null;
    #eventLinker = null;
    // What the server does not know yet (phase speichern, stage 1b): one change per box, only sendable boxes go out,
    // bundled, at most one request on its way, failures stay queued.
    #saveQueue = null;
    // 'image' | 'video' | 'pdf' — what a box needs before it may be sent (tagSendable).
    #mediaType = 'image';
    // DOM/store mismatches already reported (`tagId|domText`) — commitChanges runs on every page turn and play.
    #reportedMismatches = new Set();
    #domAdapter = null;
    #drawingAdapter = null;
    // Tags visible, nothing editable (tagsRead without tagsWrite). Fixed at construction — a session never switches.
    #readOnly = false;
    // Id source for boxes that have none yet: drawn in this session, or read from a document that still lives in
    // document_has_tag. Counts DOWNWARDS so a placeholder can never collide with a real tag_id (always positive)
    // and is recognisable as "not saved yet" everywhere. Replaced by the real id from the save response.
    #nextPlaceholderId = -1;

    // domAdapter: the DOM side of the tag boxes. Defaults to the jQuery adapter; injectable so the queue and store
    // logic can be tested without jQuery (TaggingCore.queue.test.js).
    constructor({ mediator, docId, $container, services = {}, persistence = {}, interactions = {}, captureMetadata = null, readOnly = false, domAdapter = new TagBoxJQueryAdapter() }) {
        this.#domAdapter = domAdapter;
        this.#mediator = mediator;
        this.#docId = docId;
        this.#persistence = persistence;
        this.#services = services;
        this.#captureMetadata = captureMetadata;
        this.#readOnly = readOnly;
        this.#countLiveCore();
        this.#tagStore = new TagStore(mediator, docId);
        this.#mediaType = captureMetadata?.()?.type ?? 'image';
        this.#saveQueue = this.#createSaveQueue();   // needs #persistence, #services and #mediaType set above
        const { draggable, resizable, drawing } = resolveTagInteractions({ readOnly, interactions });
        this.#drawingAdapter = drawing;
        this.#domAdapter.init($container, { ...services, docId, mediator, readOnly, isDrawing: () => drawing.isDrawing() }, { draggable, resizable });
        drawing.attach(this.#domAdapter.getTagLayer(), docId, mediator);
    }

    // Diagnostics for bug:tagging-duplicate-core-boxes-without-meta: two live cores for one document save every box
    // twice. destroy() counts down (#releaseLiveCore).
    #countLiveCore() {
        const liveCores = (TaggingCore.#liveCoresByDoc.get(this.#docId) ?? 0) + 1;
        TaggingCore.#liveCoresByDoc.set(this.#docId, liveCores);
        if (liveCores > 1) {
            this.#reportProblem(`${liveCores} live TaggingCores for document ${this.#docId} — boxes will be saved twice`);
        }
    }

    #releaseLiveCore() {
        const liveCores = (TaggingCore.#liveCoresByDoc.get(this.#docId) ?? 1) - 1;
        if (liveCores > 0) { TaggingCore.#liveCoresByDoc.set(this.#docId, liveCores); }
        else { TaggingCore.#liveCoresByDoc.delete(this.#docId); }
    }

    #createSaveQueue() {
        return new TagSaveQueue({
            sendBatch:  this.#createBatchSender(),
            isSendable: (change) => isSendableTag(change.model, this.#mediaType),
            // A delete can migrate the document too — then the response carries the ids of the boxes that stayed.
            onSaved:    (change, result) => {
                this.#adoptServerTagIds(change.type === 'delete' ? null : change.model, result.response);
                if (change.failed) { this.#publishSaveState(change, true); }   // recovered → the mark goes
            },
            // Failed changes stay queued and go out again with the next send (the status line shows the error).
            // The store does not change on a failure — tell the tag list so it can mark the box.
            onFailed:   (change) => this.#publishSaveState(change, false),
            getExcept:  () => this.#activeTagId(),
        });
    }

    // Stage 2c: the whole batch in one request when the persistence offers it (documents.js storeChangedTags →
    // admin_changetags) — it resolves with one { ok, tagId?, response? } per change. Otherwise stage 1: the
    // single-change persistence (storeChangedTag), one change after the other.
    #createBatchSender() {
        if (this.#persistence.saveBatch) { return (changes) => this.#persistence.saveBatch(this.#docId, changes); }
        return createSequentialBatchSender({
            save:   (prev, model, onOk, onErr) => this.#persistence.save   ? this.#persistence.save(this.#docId, prev, model, onOk, onErr)   : onOk(null),
            delete: (model, onOk, onErr)       => this.#persistence.delete ? this.#persistence.delete(this.#docId, model, onOk, onErr) : onOk(null),
        });
    }

    registerEvents() {
        this.#eventLinker = new TagEventLinker(this.#mediator, this.#docId);
        this.#eventLinker.registerEvents({
            onTagCreated:   (tagId, model)              => this.#saveQueue.upsert({ tagId, type: 'save', prev: null, model }),
            onTagUpdated:   (tagId, patch, prev, model) => this.#saveQueue.upsert({ tagId, type: 'save', prev, model }),
            onTagRemoved:   (tagId, model)              => this.#saveQueue.upsert({ tagId, type: 'delete', prev: model, model }),
            onStoreCleared: ()                          => this.#saveQueue.clear(),
            onUserDelete:   (tagId)                     => { this.removeTag(tagId); this.commitChanges(); },
            onUserTextChange: (tagId, newText, newLevel) => { this.updateTag(tagId, { text: newText, level: newLevel }); },
            onUserDraw:     (dbid, data)                => this.#onUserDraw(dbid, data),
            // M1a: interaction policy moved out of documents.js — the module now owns commit-on-interaction,
            // hide-on-draw-start, and text-change dedup (verhaltensneutral: same events, same tag:user:text:changed republish).
            // Another box gets active, the level picker opens, a drag/resize starts: the box that was being edited
            // has been left → send after the bundle delay (the box still being edited stays out, getExcept).
            onDomCommitExcept: ()                       => this.#saveQueue.scheduleFlush(),
            onDrawStart:       ()                       => this.#domAdapter.hideActiveInput(),
            // Background click = leaving the box (on video the same click also plays/pauses, documents.js).
            onLayerClick:      ()                       => { this.#domAdapter.hideActiveInput(); this.#saveQueue.scheduleFlush(); },
            onDomTextChanged:  (tagId, newText, newLevel) => this.#onDomTextChanged(tagId, newText, newLevel),
            // M1b: mount-dependent policy — after drag/resize a box is re-mounted; after a fresh draw it is
            // coord-converted, mounted, and its input activated. Moved out of documents.js.
            onDomDragOrResizeStop: (tagId) => this.mountTagBox(this.#domAdapter.getTagBoxById(tagId)),
            onDrawComplete: (pix) => {
                const data = this.#domAdapter.pixToPercentData(pix);
                const $tagBox = this.mountTagBox({ ...data, text: '' }, { fromDraw: true });
                this.#domAdapter.activateDrawnBox($tagBox);
            },
            // M1c: legacy-UI as injected services — the delete-confirmation dialog and the video seek stay in
            // documents.js; the module owns only the subscription + the store/commit policy.
            // Stage 5: Escape in the active box — discard a new box, reset a changed one, just close an unchanged one.
            onRevertRequest: (tagId) => this.#handleRevertRequest(Number(tagId)),
            onDomCloseClick:  (tagId)            => this.#onDomCloseClick(tagId),
            onDomTimeChanged: (tagId, startTime) => this.#onDomTimeChanged(tagId, startTime),
        });
    }

    // A drawn box arrives (fresh draw, drag/resize re-mount): an existing box moves, a new one is added with the
    // metadata of the moment (PDF page, video time).
    #onUserDraw(dbid, data) {
        const id = Number(dbid);
        const existing = this.getTag(id);
        if (existing) {
            if (isDuplicateDrawEvent(existing, data)) {
                this.#reportProblem(`draw event for existing box ${id} of document ${this.#docId} was handled twice`);
            }
            this.updateTag(id, buildRedrawPatch(existing, data));   // keeps metadata (PDF page, video start)
        } else {
            const metadata = this.#captureMetadata?.() ?? null;
            this.addTag({ id, ...data, metadata });
        }
    }

    // The box's field was left or read before an immediate send: a real change is republished as a user change
    // (dedup), then a bundled send is scheduled.
    #onDomTextChanged(tagId, newText, newLevel) {
        const id = Number(tagId);
        const current = this.getTag(id);
        if (current && (current.text === undefined || current.text !== newText || current.level !== newLevel)) {
            this.#mediator.publish('tag:user:text:changed', {
                tagId: id, newText, newLevel,
                prev: current.text !== undefined ? { ...current } : null,
                docId: this.#docId,
            });
        }
        this.#saveQueue.scheduleFlush();   // the box was left (or harvested right before an immediate send)
    }

    // The × of a box: a NEW box (never saved) is discarded exactly like with Escape — without keyword silently,
    // with keyword after "discard changes?" (stage 5c, User 2026-10-06). A saved box is deleted on the server
    // after "delete tag?" as before — resetting it stays Escape's job.
    #onDomCloseClick(tagId) {
        const id = Number(tagId);
        const model = this.getTag(id);
        if (!model) { return; }
        if (!this.#readOnly && this.#isNewTag(id)) { this.#discardNewTagAsking(id, model); return; }
        this.#services.confirmDeleteTag?.(tagId, model);
    }

    // A new start time typed at a video box: stored, sent at once, and the video jumps there.
    #onDomTimeChanged(tagId, startTime) {
        const id = Number(tagId);
        const existing = this.getTag(id);
        if (!existing) { return; }
        this.updateTag(id, { metadata: { ...existing.metadata, startTime } });
        this.commitChanges();
        this.#services.seekMediaTo?.(startTime);
    }

    // Ensure the tag box DOM exists for `input` (draw coords or an existing-box element) and register/load its
    // tag. Was documents.js mountTagBox (M2). Returns the new $tagBox, or null if the box already existed.
    mountTagBox(input, { fromDraw = false } = {}) {
        const { $b, existingId, data, pix } = this.#domAdapter.resolveInputCoords(input);
        // The box's id: the one already on the element (re-mount after drag/resize), else the real tag_id the API
        // delivered, else a placeholder. Used to be the store's COUNT — an array index that shifted whenever a box
        // was removed and meant nothing to the server (plan phase `js-echte-tagid`).
        const dbid = existingId ?? data.tagId ?? this.#nextPlaceholderId--;
        let $tagBox = null;
        if (!$b) {
            data.level = data.level || this.#domAdapter.getLastLevel() || 2;
            $tagBox = this.#domAdapter.createAndAppendTagBox(dbid, pix, {
                text: data.text, level: data.level, hideResizeHandle: data.height < 25,
            });
        }
        // Only a box the API delivers (no element yet, not drawn, not in the store) is loaded silently; everything
        // else goes through the draw event — onUserDraw adds it or moves the known box.
        const loadsFromApi = !this.getTag(Number(dbid)) && !$b && !fromDraw;
        if (loadsFromApi) {
            // tagId stays out of the model — `id` IS the tag id, and a second copy would go stale on a re-key.
            const { tagId, ...model } = data;
            this.loadTag({ id: Number(dbid), ...model });
        } else {
            this.#mediator.publish('tag:user:draw:complete', { dbid, data, pix, docId: this.#docId });
        }
        return $tagBox;
    }

    // Load all tags for this document via the injected loadTags service and mount them (M3). Returns a promise so
    // the caller can run post-load layout after. applyTagFilter runs on any resolved response (mount loop only on success).
    async loadTagsFromApi() {
        const response = await this.#services.loadTags?.(this.#docId);
        if (response && response.success) {
            for (let i = 0; i < response.data.length; i++) { this.mountTagBox(response.data[i]); }
        }
        this.applyTagFilter();
    }

    /**
     * Take over the box ids the server reported after a save.
     *
     * Two sources, and both matter: `tagId` is the id of the box that was just written (it was a placeholder
     * until now if the box is new), and `migratedBoxes` lists EVERY box of the document with its id — the server
     * sends it when this save migrated the document, so the boxes nobody touched get their ids too instead of
     * waiting for a reload. Without this the next save of such a box would still identify it by geometry.
     */
    #adoptServerTagIds(savedModel, response) {
        if (!response || response.success !== true) { return; }
        if (Number(response.tagId) > 0) { this.#rekeyTag(savedModel?.id, Number(response.tagId)); }
        if (!Array.isArray(response.migratedBoxes)) { return; }
        for (const box of response.migratedBoxes) {
            const tagId = Number(box.tagId);
            if (!(tagId > 0) || this.getTag(tagId)) { continue; }
            const match = this.getAllTags().find(tag => tag.id < 0 && TagBoxModel.matchesGeometry(tag, box));
            if (match) { this.#rekeyTag(match.id, tagId); }
        }
    }

    /** Move a box from its placeholder to the real tag_id — in the store, on the element, and in what is queued. */
    #rekeyTag(oldId, newId) {
        if (oldId === undefined || oldId === null || oldId === newId) { return; }
        if (!this.#tagStore.rekey(oldId, newId)) { return; }
        this.#domAdapter.setTagBoxId(oldId, newId);
        this.#saveQueue.rekey(oldId, newId);
    }

    // Send what the server does not know yet — right away (closing, page turn, play, deleted box, changed start time).
    // The open input is read into the store first, so text typed into a box that was never left goes out too (W1/W2).
    // onAllDone fires when nothing sendable is left; boxes that cannot be sent yet (no keyword, no page …) stay in memory.
    // waitForFlash: when something was saved, call onAllDone only after the green/red flash (a PDF page change, User
    // 2026-10-08). A failing flush still calls onAllDone — a caller that locked its controls must get them back.
    commitChanges(onAllDone, { waitForFlash = false } = {}) {
        if (this.#discardIfReadOnly(onAllDone)) { return; }
        this.#domAdapter.harvestActiveInput();
        this.#reportDomStoreMismatches();
        this.#saveQueue.flushNow()
            .catch((err) => { this.#reportProblem(`commitChanges: flush failed — ${err?.message ?? err}`); return { saved: false }; })
            .then(({ saved } = {}) => {
                if (waitForFlash && saved) { setTimeout(() => onAllDone?.(), FLASH_MS); } else { onAllDone?.(); }
            });
    }

    // Measurement (fehler-sichtbar F3): a box that shows other keywords than the store holds would be saved with the
    // store's — report it (console + server log), correct nothing.
    #reportDomStoreMismatches() {
        const domBoxes = this.#domAdapter.readBoxTexts?.() ?? [];
        for (const m of findDomStoreMismatches(domBoxes, (id) => this.getTag(id))) {
            const key = `${m.tagId}|${m.domText}`;
            if (this.#reportedMismatches.has(key)) { continue; }
            this.#reportedMismatches.add(key);
            this.#reportProblem(`DOM/store mismatch (${m.reason}) for box ${m.tagId} of document ${this.#docId}: `
                + `DOM ${JSON.stringify(m.domText)}, store ${JSON.stringify(m.storeText)}`);
        }
    }

    // Boxes whose change the server does not have and that are not on their way → Map(tagId → reason):
    // an UnsendableReason (cannot be sent as it is, stage 1c) or SAVE_FAILED_REASON (the last send failed, F2).
    // A stored box nobody touched is not in it, even if incomplete — it is saved. A failed delete is in it too
    // (the box is gone from the store but still on the server). Read by the tag list and on closing.
    getUnsavedReasons() {
        const reasons = new Map();
        for (const change of this.#saveQueue.pending()) {
            const reason = this.#unsavedReason(change);
            if (reason !== null) { reasons.set(change.tagId, reason); }
        }
        return reasons;
    }

    // Why a queued change is not saved, or null: it cannot be sent as it is (a delete always can), else its last
    // send failed.
    #unsavedReason(change) {
        const unsendable = change.type === 'delete' ? null : unsendableReason(change.model, this.#mediaType);
        if (unsendable !== null) { return unsendable; }
        return change.failed ? SAVE_FAILED_REASON : null;
    }

    // Escape in the active box (plan bugfix_2026-10-06_permission-tagging, phase speichern, stage 5). "New" = never
    // saved (its queue entry has no prev). What counts is what the box holds now: the open field and the store.
    //   A new, no keyword      → removed without asking
    //   B new, with keywords   → services.confirmRevertTag('discard-new') → removed
    //   C saved, changed       → services.confirmRevertTag('reset') → back to the saved state (text, level, geometry,
    //                            metadata) — the queue keeps its prev, so the next send finds nothing to save
    //   D saved, unchanged     → the field closes as on leaving
    #handleRevertRequest(id) {
        if (this.#readOnly) { return; }
        const current = this.getTag(id);
        if (!current) { return; }
        if (this.#isNewTag(id)) { this.#discardNewTagAsking(id, current); return; }
        const typed = this.#typedInput(id, current);
        const entry = this.#pendingEntry(id);
        if (!this.#hasUnsavedChange(entry, typed, current)) { this.#domAdapter.hideActiveInput(); return; }
        const saved = entry?.prev ?? current;
        this.#services.confirmRevertTag?.('reset', () => this.#resetToSaved(id, saved), () => this.#domAdapter.focusActiveInput?.());
    }

    // "New" = never saved: its queue entry has no prev (or, without an entry, it still has a placeholder id).
    #isNewTag(id) {
        const entry = this.#pendingEntry(id);
        return entry ? entry.prev === null : id < 0;
    }

    #pendingEntry(id) { return this.#saveQueue.pending().find((c) => c.tagId === id); }

    // A change the server does not have: queued (moved, retyped, failed …) or only typed into the open field.
    #hasUnsavedChange(entry, typed, current) {
        return entry !== undefined
            || keywordKey(typed.text) !== keywordKey(current.text)
            || String(typed.level) !== String(current.level);
    }

    // What the box holds now: the open field if it is this box's, else the store.
    #typedInput(id, current) {
        const typed = this.#domAdapter.readActiveInput?.();
        return typed && Number(typed.tagId) === id ? typed : { text: current.text, level: current.level };
    }

    // A new box goes: without keyword silently (A), with keyword after "discard changes?" (B). Escape and the ×.
    #discardNewTagAsking(id, current) {
        if (!keywordKey(this.#typedInput(id, current).text) && !keywordKey(current.text)) { this.#discardNewTag(id); return; }
        this.#services.confirmRevertTag?.('discard-new', () => this.#discardNewTag(id), () => this.#domAdapter.focusActiveInput?.());
    }

    #discardNewTag(id) {
        // Only close the open field if it is this box's — the × can belong to a box that is not the active one.
        if (Number(this.#domAdapter.getActiveTagId?.()) === id) { this.#domAdapter.cancelActiveInput(); }
        this.#domAdapter.removeTagBox(id);
        this.removeTag(id);   // the queue drops a never-sent create (or deletes after a create on its way)
    }

    #resetToSaved(id, saved) {
        this.#domAdapter.cancelActiveInput();
        const { text, level, left, top, width, height, metadata } = saved;
        const model = this.updateTag(id, { text, level, left, top, width, height, metadata: metadata ?? null });
        if (model) { this.#domAdapter.showModelInBox(model); }
        this.applyTagFilter();               // another page / start time may hide or show it now
        this.#saveQueue.scheduleFlush();     // the queue entry now equals the saved state → goes away unsent
    }

    #publishSaveState(change, ok) {
        this.#mediator.publish('tag:save:state', { tagId: change.tagId, ok, docId: this.#docId });
    }

    #activeTagId() {
        const id = this.#domAdapter.getActiveTagId?.();
        return id === null || id === undefined ? null : Number(id);
    }

    // Second net for read-only: the UI offers no editing, but anything that still reached the queue is dropped
    // instead of being sent (the server would refuse it anyway — tagsWrite).
    #discardIfReadOnly(onAllDone) {
        if (!this.#readOnly) { return false; }
        this.#saveQueue.clear();
        onAllDone?.();
        return true;
    }

    // A problem the user would not notice: to the console AND, via the injected reportProblem service, to the server
    // log — a customer's console is usually gone by the time the problem is reported.
    #reportProblem(message) {
        const stack = new Error().stack;
        console.warn(`[tagging] ${message}`, stack);
        this.#services.reportProblem?.(message, stack);
    }

    destroy() {
        if (!this.#destroyed) {   // count each core down once, even if destroy() runs twice
            this.#destroyed = true;
            this.#releaseLiveCore();
        }
        this.#eventLinker?.unregisterEvents();
        this.#eventLinker = null;
        this.#saveQueue.clear();
        this.#drawingAdapter.detach();
        this.#domAdapter.destroy();
    }

    getDomAdapter() { return this.#domAdapter; }

    repositionAllTagDoms() { this.#domAdapter.repositionAllTagDoms(this.getAllTags()); }

    setupTagLayer($img) {
        this.#domAdapter.configureLayer($img);
        const $layer = this.#domAdapter.getTagLayer();
        $layer
            .on('beforeResize', () => {
                this.#domAdapter.hideActiveInput();
                $layer.css('opacity', 0);
            })
            .on('afterResize', () => {
                this.repositionAllTagDoms();
                $layer.fadeTo(100, 1);
            });
    }

    // Store delegation — documents.js (and Phase 4 user-action handlers) access state here
    getTag(id)              { return this.#tagStore.get(id); }
    getAllTags()             { return this.#tagStore.getAll(); }
    addTag(model)           { return this.#tagStore.add(model); }
    loadTag(model)          { return this.#tagStore.load(model); }
    updateTag(id, patch)    { return this.#tagStore.update(id, patch); }
    removeTag(id)           { return this.#tagStore.remove(id); }

    // Fill in metadata of a box (e.g. a missing PDF page from the tag list) and save it right away, like a changed
    // video start time. Returns false when nothing was changed (read-only session or unknown box).
    updateTagMetadata(id, patch) {
        const existing = this.getTag(id);
        if (this.#readOnly || !existing) { return false; }
        this.updateTag(id, { metadata: { ...(existing.metadata ?? {}), ...patch } });
        this.commitChanges();
        return true;
    }

    setTagsVisible(on)      { this.#domAdapter.setLayerInteractive(on); }

    isReadOnly()            { return this.#readOnly; }

    reconfigureLayer($img)  { this.#domAdapter.configureLayer($img); }

    setTagFilter(fn) {
        this.#tagFilter = fn;
        this.applyTagFilter();
    }

    applyTagFilter() {
        if (!this.#tagFilter) return;
        this.#domAdapter.applyTagFilter(this.getAllTags(), this.#tagFilter);
    }
}
